import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../components/Icon';
import { Button, useActionDialog } from '../../../components/ui';
import type { CycleStatus, StageKind, TreatmentStage } from '../../../types/admin';
import { PreparingPanel } from './PreparingPanel';
import { ModelingPanel } from './ModelingPanel';
import { DeliveryPanel } from './DeliveryPanel';
import { MovementStepsPanel } from './MovementStepsPanel';
import { WearingPanel } from './WearingPanel';
import { useUpdateCycleStatus, useUpdateTreatmentStage } from '../hooks/usePatientMutations';
import styles from './StageEditor.module.scss';

type StageEditorProps = {
  readonly patientId: string;
  readonly treatmentId: string;
  readonly stage: TreatmentStage;
  readonly isPatientVisibleCycle: boolean;
  readonly hasOtherInProgressCycle?: boolean;
  readonly hasNextCycle: boolean;
};

export function StageEditor({
  patientId,
  treatmentId,
  stage,
  isPatientVisibleCycle,
  hasOtherInProgressCycle = false,
  hasNextCycle,
}: StageEditorProps) {
  const { t } = useTranslation();
  const { confirmAction } = useActionDialog();
  const TABS: readonly { readonly kind: StageKind; readonly label: string }[] = [
    { kind: 'preparing', label: t('stageEditor.preparing') },
    { kind: 'modeling', label: t('stageEditor.modeling') },
    { kind: 'payment', label: t('stageEditor.payment') },
    { kind: 'manufacturing_delivery', label: t('stageEditor.delivery') },
    { kind: 'wearing_aligners', label: t('stageEditor.wearing') },
  ];
  const activeStep = useMemo(
    () => findActiveStep(stage),
    [stage],
  );
  const patientStageKind: StageKind = stage.current_stage_kind ?? activeStep?.stage_kind ?? 'preparing';
  const [activeTab, setActiveTab] = useState<StageKind>(patientStageKind);
  const [isCollapsed, setIsCollapsed] = useState(false);
  // Movement-step rows that hold no mesh yet. Owned here because
  // MovementStepsPanel is mounted on both the payment and delivery tabs, and
  // per-instance state would drop a pending row on every tab switch.
  const [movementDraftSteps, setMovementDraftSteps] = useState<readonly number[]>([]);
  const updateStageMutation = useUpdateTreatmentStage(patientId, treatmentId, stage.id);
  const cycleStatusMutation = useUpdateCycleStatus(patientId, treatmentId, stage.id);
  const cycleStatus: CycleStatus = stage.status ?? 'in_progress';
  const cycleStatusLabels: Record<CycleStatus, string> = {
    draft: t('stageEditor.cycleStatus.draft', { defaultValue: 'Чернетка' }),
    in_progress: t('stageEditor.cycleStatus.in_progress', { defaultValue: 'У роботі' }),
    completed: t('stageEditor.cycleStatus.completed', { defaultValue: 'Завершено' }),
    cancelled: t('stageEditor.cycleStatus.cancelled', { defaultValue: 'Скасовано' }),
  };

  async function handleFinishCycle() {
    if (!hasNextCycle || cycleStatusMutation.isPending) return;
    if (!(await confirmAction({
      message: t('stageEditor.finishCycleConfirm', { defaultValue: 'Завершити етап?' }),
      confirmLabel: t('stageEditor.finishCycle'),
      tone: 'warning',
      icon: 'check',
    }))) {
      return;
    }
    try {
      await cycleStatusMutation.mutateAsync('completed');
    } catch {
      return;
    }
  }

  async function handleResumeCycle() {
    try {
      await cycleStatusMutation.mutateAsync('in_progress');
    } catch {
      return;
    }
  }

  async function handleActivateCycle() {
    try {
      await cycleStatusMutation.mutateAsync('in_progress');
    } catch {
      return;
    }
  }

  async function handleCancelCycle() {
    if (!(await confirmAction({
      message: t('stageEditor.cancelCycleConfirm', { defaultValue: 'Скасувати етап?' }),
      confirmLabel: t('stageEditor.cancelCycle'),
      tone: 'danger',
    }))) {
      return;
    }
    try {
      await cycleStatusMutation.mutateAsync('cancelled');
    } catch {
      return;
    }
  }
  useEffect(() => {
    setActiveTab(stage.current_stage_kind ?? activeStep?.stage_kind ?? 'preparing');
  }, [activeStep?.stage_kind, stage.current_stage_kind]);

  async function handleMovePatientToActiveTab() {
    if (activeTab === patientStageKind || updateStageMutation.isPending) return;

    const activeTabLabel = TABS.find((tab) => tab.kind === activeTab)?.label ?? '';
    if (!(await confirmAction({
      title: t('stageEditor.changeStageTitle'),
      message: t('stageEditor.movePatientConfirm', { stage: activeTabLabel }),
      confirmLabel: t('stageEditor.movePatientHere'),
      tone: 'warning',
      icon: 'person',
    }))) {
      return;
    }

    try {
      await updateStageMutation.mutateAsync({ current_stage_kind: activeTab });
    } catch {
      return;
    }
  }

  const panelId = `cycle-${stage.id}-content`;

  function handleTabKeyDown(event: KeyboardEvent<HTMLButtonElement>, tabIndex: number) {
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') nextIndex = (tabIndex + 1) % TABS.length;
    if (event.key === 'ArrowLeft') nextIndex = (tabIndex - 1 + TABS.length) % TABS.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = TABS.length - 1;
    if (nextIndex === null) return;

    event.preventDefault();
    const nextTab = TABS[nextIndex];
    setActiveTab(nextTab.kind);
    requestAnimationFrame(() => document.getElementById(`${stage.id}-${nextTab.kind}-tab`)?.focus());
  }

  return (
    <div className={styles.editor}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.stageLabel}>
            {t('stageEditor.stageN', { number: stage.cycle_number })}
            {cycleStatus !== 'in_progress' && (
              <span className={styles.stageStatusInline}> · {cycleStatusLabels[cycleStatus]}</span>
            )}
          </span>
        </div>
        <div className={styles.headerActions}>
          {cycleStatus === 'draft' && !hasOtherInProgressCycle ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => void handleActivateCycle()}
              disabled={cycleStatusMutation.isPending}
            >
              {cycleStatusMutation.isPending
                ? t('stageEditor.activatingCycle', { defaultValue: 'Активація...' })
                : t('stageEditor.activateCycle', { defaultValue: 'Активувати етап' })}
            </Button>
          ) : cycleStatus === 'in_progress' ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => void handleFinishCycle()}
              disabled={cycleStatusMutation.isPending || !hasNextCycle}
              aria-describedby={!hasNextCycle ? `cycle-${stage.id}-finish-hint` : undefined}
            >
              {cycleStatusMutation.isPending
                ? t('stageEditor.finishingCycle', { defaultValue: 'Завершення...' })
                : t('stageEditor.finishCycle', { defaultValue: 'Завершити етап' })}
            </Button>
          ) : !hasOtherInProgressCycle ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void handleResumeCycle()}
              disabled={cycleStatusMutation.isPending}
            >
              {cycleStatusMutation.isPending
                ? t('stageEditor.resumingCycle', { defaultValue: 'Відновлення...' })
                : t('stageEditor.resumeCycle', { defaultValue: 'Відновити етап' })}
            </Button>
          ) : null}
          {cycleStatus === 'in_progress' && !hasNextCycle && (
            <span id={`cycle-${stage.id}-finish-hint`} className={styles.finishHint}>
              {t('stageEditor.finishCycleRequiresNext')}
            </span>
          )}
          {cycleStatusMutation.error instanceof Error && (
            <span className={styles.errorHint}>{cycleStatusMutation.error.message}</span>
          )}
          <button
            type="button"
            className={`${styles.collapseButton} ${isCollapsed ? styles.collapseButtonCollapsed : ''}`}
            onClick={() => setIsCollapsed((prev) => !prev)}
            aria-label={t('stageEditor.toggleStage', { defaultValue: 'Розгорнути або згорнути етап' })}
            aria-expanded={!isCollapsed}
            aria-controls={!isCollapsed ? panelId : undefined}
          >
            <Icon name="chevron-down" size={14} />
          </button>
        </div>
      </div>

      {!isCollapsed && <div id={panelId}>
        <div className={styles.tabBar} role="tablist">
          {TABS.map((tab, tabIndex) => {
            const isPatientStage = stage.current_stage_kind === tab.kind;
            return (
              <button
                key={tab.kind}
                type="button"
                id={`${stage.id}-${tab.kind}-tab`}
                role="tab"
                aria-selected={activeTab === tab.kind}
                aria-controls={`${stage.id}-active-panel`}
                tabIndex={activeTab === tab.kind ? 0 : -1}
                className={`${styles.tab} ${activeTab === tab.kind ? styles.tabActive : ''}`}
                onClick={() => setActiveTab(tab.kind)}
                onKeyDown={(event) => handleTabKeyDown(event, tabIndex)}
              >
                <span>{tab.label}</span>
                {isPatientVisibleCycle && isPatientStage && (
                  <span
                    className={styles.tabPatientBadge}
                    title={t('stageEditor.patientAtThisStage')}
                  >
                    <Icon name="person" size={10} />
                    {t('stageEditor.patientTabBadge')}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {activeTab !== patientStageKind && (
          <div className={styles.movePatientBar} role="status">
            <span>
              {t('stageEditor.patientRemainsAt', {
                stage: TABS.find((tab) => tab.kind === patientStageKind)?.label ?? '',
              })}
            </span>
            <Button
              variant="primary"
              size="sm"
              onClick={() => void handleMovePatientToActiveTab()}
              disabled={updateStageMutation.isPending}
            >
              {updateStageMutation.isPending
                ? t('stageEditor.saving')
                : t('stageEditor.movePatientHere')}
            </Button>
          </div>
        )}
        {updateStageMutation.error instanceof Error && (
          <p className={styles.movePatientError} role="alert">
            {t('stageEditor.failedToSave')}: {updateStageMutation.error.message}
          </p>
        )}

        <div
          id={`${stage.id}-active-panel`}
          className={styles.panelContainer}
          role="tabpanel"
          aria-labelledby={`${stage.id}-${activeTab}-tab`}
        >
          {activeTab === 'preparing' && (
            <PreparingPanel
              patientId={patientId}
              treatmentId={treatmentId}
              stage={stage}
            />
          )}
          {activeTab === 'modeling' && (
            <ModelingPanel
              patientId={patientId}
              treatmentId={treatmentId}
              stage={stage}
            />
          )}
          {activeTab === 'payment' && (
            <>
              <div className={styles.paymentRedirect}>
                <span className={styles.paymentRedirectIcon} aria-hidden="true">
                  <Icon name="payment" size={20} />
                </span>
                <p>{t('stageEditor.paymentMovedNotice')}</p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    if (typeof window === 'undefined') return;
                    window.dispatchEvent(new CustomEvent('waf:open-payments'));
                  }}
                >
                  {t('stageEditor.paymentOpenSection')}
                </Button>
              </div>
              <MovementStepsPanel
                patientId={patientId}
                treatmentId={treatmentId}
                stage={stage}
                draftSteps={movementDraftSteps}
                onDraftStepsChange={setMovementDraftSteps}
              />
            </>
          )}
          {activeTab === 'manufacturing_delivery' && (
            <>
              <DeliveryPanel
                patientId={patientId}
                treatmentId={treatmentId}
                stage={stage}
              />
              <MovementStepsPanel
                patientId={patientId}
                treatmentId={treatmentId}
                stage={stage}
                draftSteps={movementDraftSteps}
                onDraftStepsChange={setMovementDraftSteps}
              />
            </>
          )}
          {activeTab === 'wearing_aligners' && (
            <WearingPanel
              patientId={patientId}
              treatmentId={treatmentId}
              stage={stage}
            />
          )}
        </div>
      </div>}

    </div>
  );
}

function findActiveStep(stage: TreatmentStage) {
  const orderedSteps = [...stage.steps].sort((a, b) => a.sort_order - b.sort_order);
  if (stage.current_stage_kind) {
    const currentStep = orderedSteps.find((step) => step.stage_kind === stage.current_stage_kind);
    if (currentStep) {
      return currentStep;
    }
  }
  return orderedSteps.find((step) => step.status === 'in_progress' || step.status === 'blocked') ?? null;
}
